import React, { createContext, useContext, useReducer } from 'react'
import { toastService } from '../services/toastService'
import { apiClient } from '../services/apiClient'

export const FALLBACK_PROVIDERS = {
  openai: {
    name: 'OpenAI',
    models: [
      'gpt-5.4', 'gpt-5.4-mini', 'gpt-5.3-codex',
      'gpt-5.2-pro', 'gpt-5.2', 'gpt-5.1', 'gpt-5', 'gpt-5-mini', 'gpt-5-nano',
      'gpt-4o', 'gpt-4o-mini', 'gpt-4.1', 'gpt-4.1-mini', 'gpt-3.5-turbo',
      'o1-preview', 'o1-mini'
    ]
  },
  anthropic: {
    name: 'Anthropic',
    models: [
      'claude-opus-4-7', 'claude-sonnet-4-6',
      'claude-opus-4-6', 'claude-opus-4-5', 'claude-sonnet-4-5', 'claude-haiku-4-5',
      'claude-opus-4-1', 'claude-3-5-sonnet-20241022', 'claude-3-5-haiku-20241022'
    ]
  }
}

// Initial state
const initialState = {
  // User query and settings
  userQuery: "",
  contextEnabled: true,
  llmEnabled: false,

  
  // UI state
  isLoading: false,
  activeModal: null, // 'knowledge', 'memory', 'llm-config'
  activeTab: 'memory', // 'memory', 'ingestion'
  activeAgentsTab: 'responses',
  activeUserTab: 'query', // 'query', 'configuration'
  memoryViewMode: 'table', // 'table', 'card'
  theme: 'dark', // 'light', 'dark'
  
  // LLM Configuration
  supervisorLLM: { 
    provider: 'openai', 
    model: 'gpt-4o-mini' 
  }, // Selected LLM for supervisor (defaults to OpenAI GPT-4o-mini, uses server env vars for API keys)
  agentsLLMEnabled: false, // Toggle for agents-level LLM usage
  enabledAgents: { // Which agents are enabled for usage
    openai: false,
    anthropic: false,
    google: false
  },
  allowUserModelSelection: false, // Allow users to choose model versions in chat
  
  // Summarization (Organization LLM → Summarization); default when nothing configured
  summaryConfig: { provider: 'ollama', model: 'gemma3:1b' },
  
  // Auto Mode Configuration
  autoModeEnabled: false, // Toggle for automatic query routing
  autoModeRules: [], // Rules for automatic query routing
  autoModeManagedBySupervisor: false, // Toggle for supervisor-managed routing
  systemProviders: FALLBACK_PROVIDERS,
  
  // Data
  finalAnswer: null,
  memoryData: [],
  retrievedContext: [], // Contexto recuperado de la base de datos
  agentResponses: [],
  knowledgeStats: {
    totalDocuments: 5,
    connectedPlatforms: 3,
    apiEndpoints: 3,
    lastUpdated: '2 min ago'
  },
  memoryStats: {
    totalEntries: 0,
    highConfidence: 0,
    mediumConfidence: 0,
    totalAgentCalls: 0
  },
  
  // Flow visualization
  flowStatus: 'idle', // 'idle', 'processing', 'completed'
  activeNodes: [],
  statusMessages: [],
  flowVisible: false, // Hide flow by default
  
  // Chat history
  currentSessionId: null,
  chatMessages: [],
  chatHistoryVisible: false,

  // Workspace
  activeWorkspace: null,
  
  // Toast notifications
  toasts: [], // { id, message, type: 'success' | 'error' | 'warning' | 'info', duration }
  
  // User permissions
  userPermissions: [], // Loaded from getMyPermissions API
  permissionsLoaded: false
}

// Action types
const ActionTypes = {
  SET_USER_QUERY: 'SET_USER_QUERY',
  SET_CONTEXT_ENABLED: 'SET_CONTEXT_ENABLED',
  SET_LLM_ENABLED: 'SET_LLM_ENABLED',

  SET_LOADING: 'SET_LOADING',
  SET_ACTIVE_MODAL: 'SET_ACTIVE_MODAL',
  SET_ACTIVE_TAB: 'SET_ACTIVE_TAB',
  SET_ACTIVE_USER_TAB: 'SET_ACTIVE_USER_TAB',
  SET_MEMORY_VIEW_MODE: 'SET_MEMORY_VIEW_MODE',
  SET_FINAL_ANSWER: 'SET_FINAL_ANSWER',
  SET_MEMORY_DATA: 'SET_MEMORY_DATA',
  SET_RETRIEVED_CONTEXT: 'SET_RETRIEVED_CONTEXT',
  SET_AGENT_RESPONSES: 'SET_AGENT_RESPONSES',
  UPDATE_MEMORY_STATS: 'UPDATE_MEMORY_STATS',
  SET_FLOW_STATUS: 'SET_FLOW_STATUS',
  SET_ACTIVE_NODES: 'SET_ACTIVE_NODES',
  ADD_STATUS_MESSAGE: 'ADD_STATUS_MESSAGE',
  CLEAR_STATUS_MESSAGES: 'CLEAR_STATUS_MESSAGES',
  SET_FLOW_VISIBLE: 'SET_FLOW_VISIBLE',
  SET_SUPERVISOR_LLM: 'SET_SUPERVISOR_LLM',
  SET_AGENTS_LLM_ENABLED: 'SET_AGENTS_LLM_ENABLED',
  SET_ENABLED_AGENTS: 'SET_ENABLED_AGENTS',
  SET_ALLOW_USER_MODEL_SELECTION: 'SET_ALLOW_USER_MODEL_SELECTION',
  SET_SUMMARY_CONFIG: 'SET_SUMMARY_CONFIG',
  SET_AUTO_MODE_ENABLED: 'SET_AUTO_MODE_ENABLED',
  SET_AUTO_MODE_RULES: 'SET_AUTO_MODE_RULES',
  SET_AUTO_MODE_MANAGED_BY_SUPERVISOR: 'SET_AUTO_MODE_MANAGED_BY_SUPERVISOR',
  SET_SYSTEM_PROVIDERS: 'SET_SYSTEM_PROVIDERS',
  SET_CURRENT_SESSION: 'SET_CURRENT_SESSION',
  SET_CHAT_MESSAGES: 'SET_CHAT_MESSAGES',
  ADD_CHAT_MESSAGE: 'ADD_CHAT_MESSAGE',
  LOAD_CHAT_MESSAGES: 'LOAD_CHAT_MESSAGES',
  CLEAR_CURRENT_CHAT: 'CLEAR_CURRENT_CHAT',
  SET_ACTIVE_WORKSPACE: 'SET_ACTIVE_WORKSPACE',
  TOGGLE_CHAT_HISTORY: 'TOGGLE_CHAT_HISTORY',
  SET_THEME: 'SET_THEME',
  ADD_TOAST: 'ADD_TOAST',
  REMOVE_TOAST: 'REMOVE_TOAST',
  SET_USER_PERMISSIONS: 'SET_USER_PERMISSIONS',
  SET_PERMISSIONS_LOADED: 'SET_PERMISSIONS_LOADED',
  SET_AGENT_MODELS: 'SET_AGENT_MODELS'
}

// Reducer
function appReducer(state, action) {
  switch (action.type) {
    case ActionTypes.SET_USER_QUERY:
      return { ...state, userQuery: action.payload }
    
    case ActionTypes.SET_CONTEXT_ENABLED: {
      // ALWAYS force contextEnabled to true - never allow it to be disabled
      const forcedValue = true
      if (action.payload === false) {
        console.warn('Attempted to disable context, but context must always be enabled. Keeping it TRUE.')
      }
      // Persist to localStorage
      localStorage.setItem('context-enabled', 'true')
      return { ...state, contextEnabled: forcedValue }
    }
    
    case ActionTypes.SET_LLM_ENABLED:
      return { ...state, llmEnabled: action.payload }
    

    
    case ActionTypes.SET_LOADING:
      return { ...state, isLoading: action.payload }
    
    case ActionTypes.SET_ACTIVE_MODAL:
      return { ...state, activeModal: action.payload }
    
    case ActionTypes.SET_ACTIVE_TAB:
      return { ...state, activeTab: action.payload }
    
    case ActionTypes.SET_ACTIVE_USER_TAB:
      return { ...state, activeUserTab: action.payload }
    
    case ActionTypes.SET_MEMORY_VIEW_MODE:
      return { ...state, memoryViewMode: action.payload }
    
    case ActionTypes.SET_FINAL_ANSWER:
      return { ...state, finalAnswer: action.payload }
    
    case ActionTypes.SET_MEMORY_DATA:
      return { ...state, memoryData: action.payload }
    
    case ActionTypes.SET_RETRIEVED_CONTEXT:
      return { ...state, retrievedContext: action.payload }
    
    case ActionTypes.SET_AGENT_RESPONSES:
      return { ...state, agentResponses: action.payload }
    
    case ActionTypes.UPDATE_MEMORY_STATS:
      return { ...state, memoryStats: { ...state.memoryStats, ...action.payload } }
    
    case ActionTypes.SET_FLOW_STATUS:
      return { ...state, flowStatus: action.payload }
    
    case ActionTypes.SET_ACTIVE_NODES:
      return { ...state, activeNodes: action.payload }
    
    case ActionTypes.ADD_STATUS_MESSAGE:
      return { 
        ...state, 
        statusMessages: [...state.statusMessages, action.payload]
      }
    
    case ActionTypes.CLEAR_STATUS_MESSAGES:
      return { ...state, statusMessages: [] }
    
    case ActionTypes.SET_FLOW_VISIBLE:
      console.log('SET_FLOW_VISIBLE reducer called:', { currentState: state.flowVisible, newValue: action.payload })
      return { ...state, flowVisible: action.payload }
    
    case ActionTypes.SET_SUPERVISOR_LLM:
      return { ...state, supervisorLLM: action.payload }
    
    case ActionTypes.SET_AGENTS_LLM_ENABLED:
      return { ...state, agentsLLMEnabled: action.payload }
    
    case ActionTypes.SET_ENABLED_AGENTS:
      return { ...state, enabledAgents: action.payload }

    case ActionTypes.SET_AGENT_MODELS:
      return { ...state, agentModels: action.payload }
    
    case ActionTypes.SET_ALLOW_USER_MODEL_SELECTION:
      return { ...state, allowUserModelSelection: action.payload }
    
    case ActionTypes.SET_SUMMARY_CONFIG:
      return { ...state, summaryConfig: action.payload }
    
    case ActionTypes.SET_AUTO_MODE_ENABLED:
      return { ...state, autoModeEnabled: action.payload }
    
    case ActionTypes.SET_AUTO_MODE_RULES:
      return { ...state, autoModeRules: action.payload }
    
    case ActionTypes.SET_AUTO_MODE_MANAGED_BY_SUPERVISOR:
      console.log('AppContext: SET_AUTO_MODE_MANAGED_BY_SUPERVISOR', action.payload)
      return { ...state, autoModeManagedBySupervisor: action.payload }

    case ActionTypes.SET_SYSTEM_PROVIDERS:
      return { ...state, systemProviders: action.payload }
    
    case ActionTypes.SET_CURRENT_SESSION:
      return { ...state, currentSessionId: action.payload }
    
    case ActionTypes.SET_CHAT_MESSAGES:
      return { ...state, chatMessages: action.payload }
    
    case ActionTypes.ADD_CHAT_MESSAGE:
      return { 
        ...state, 
        chatMessages: [...state.chatMessages, action.payload]
      }
    
    case ActionTypes.LOAD_CHAT_MESSAGES:
      return { ...state, chatMessages: action.payload }
    
    case ActionTypes.CLEAR_CURRENT_CHAT:
      return { ...state, chatMessages: [], userQuery: '' }

    case ActionTypes.SET_ACTIVE_WORKSPACE:
      return { ...state, activeWorkspace: action.payload }
    
    case ActionTypes.TOGGLE_CHAT_HISTORY:
      return { ...state, chatHistoryVisible: !state.chatHistoryVisible }
    
    case ActionTypes.SET_THEME:
      return { ...state, theme: action.payload }
    
    case ActionTypes.ADD_TOAST:
      return { 
        ...state, 
        toasts: [...state.toasts, action.payload]
      }
    
    case ActionTypes.REMOVE_TOAST:
      return { 
        ...state, 
        toasts: state.toasts.filter(toast => toast.id !== action.payload)
      }
    
    case ActionTypes.SET_USER_PERMISSIONS:
      return { ...state, userPermissions: action.payload }
    
    case ActionTypes.SET_PERMISSIONS_LOADED:
      return { ...state, permissionsLoaded: action.payload }
    
    default:
      return state
  }
}

// Context
const AppContext = createContext()

// Provider component
export function AppProvider({ children }) {
  const [state, dispatch] = useReducer(appReducer, initialState)
  
  // Toast helper function
  const showToast = React.useCallback((message, type = 'info', duration = 3000) => {
    const id = Date.now() + Math.random()
    dispatch({
      type: ActionTypes.ADD_TOAST,
      payload: { id, message, type, duration }
    })
    
    // Auto-dismiss
    setTimeout(() => {
      dispatch({ type: ActionTypes.REMOVE_TOAST, payload: id })
    }, duration)
  }, [])

  const setActiveWorkspace = React.useCallback((workspace) => {
    dispatch({ type: ActionTypes.SET_ACTIVE_WORKSPACE, payload: workspace })
  }, [])
  
  // Register showToast with the global toast service on mount
  React.useEffect(() => {
    toastService.register(showToast)
  }, [showToast])

  React.useEffect(() => {
    const loadSystemProviders = async () => {
      const providers = await apiClient.getSystemProviders()
      if (providers && typeof providers === 'object') {
        dispatch({ type: ActionTypes.SET_SYSTEM_PROVIDERS, payload: providers })
      }
    }

    loadSystemProviders()
  }, [])
  
  // Function to load user permissions (can be called from outside)
  const loadUserPermissions = React.useCallback(async () => {
    try {
      // Check if user is authenticated
      const authToken = localStorage.getItem('cognito_id_token')
      if (!authToken) {
        console.log('No auth token found, skipping permission load')
        dispatch({ type: ActionTypes.SET_PERMISSIONS_LOADED, payload: true })
        dispatch({ type: ActionTypes.SET_USER_PERMISSIONS, payload: [] })
        return
      }
      
      // Reset permissions loaded state while loading
      dispatch({ type: ActionTypes.SET_PERMISSIONS_LOADED, payload: false })
      
      // Try to load from cache first for immediate display
      const cachedPermissions = localStorage.getItem('user-permissions')
      if (cachedPermissions) {
        try {
          const permissions = JSON.parse(cachedPermissions)
          dispatch({ type: ActionTypes.SET_USER_PERMISSIONS, payload: permissions })
          console.log('Loaded cached permissions:', permissions.length, 'permissions')
        } catch (e) {
          console.warn('Failed to parse cached permissions:', e)
        }
      }
      
      // Load fresh permissions from API
      const { authorizationApi } = await import('../services/authorizationApiService')
      const response = await authorizationApi.getMyPermissions()
      const permissions = response.permissions || []
      
      dispatch({ type: ActionTypes.SET_USER_PERMISSIONS, payload: permissions })
      dispatch({ type: ActionTypes.SET_PERMISSIONS_LOADED, payload: true })
      
      // Cache for future use
      localStorage.setItem('user-permissions', JSON.stringify(permissions))
      console.log('Loaded user permissions:', permissions.length, 'permissions')
    } catch (error) {
      console.error('Error loading user permissions:', error)
      // Still mark as loaded to prevent infinite loading state
      dispatch({ type: ActionTypes.SET_PERMISSIONS_LOADED, payload: true })
    }
  }, [])
  
  // Load user permissions on mount and when auth token changes
  React.useEffect(() => {
    loadUserPermissions()
    
    // Listen for storage changes (when token is set/removed)
    const handleStorageChange = (e) => {
      if (e.key === 'cognito_id_token') {
        console.log('Auth token changed, reloading permissions')
        loadUserPermissions()
      }
    }
    
    // Listen for custom event when user signs in
    const handleAuthChange = () => {
      console.log('Auth state changed, reloading permissions')
      // Small delay to ensure token is stored
      setTimeout(() => {
        loadUserPermissions()
      }, 200)
    }
    
    window.addEventListener('storage', handleStorageChange)
    window.addEventListener('auth-state-changed', handleAuthChange)
    
    // Also check periodically if token exists but permissions aren't loaded
    // This is a fallback in case events don't fire
    const checkInterval = setInterval(() => {
      const authToken = localStorage.getItem('cognito_id_token')
      if (authToken) {
        // Check if permissions are loaded by checking state
        // We use a ref-like approach by checking localStorage cache
        const cachedPermissions = localStorage.getItem('user-permissions')
        if (!cachedPermissions) {
          console.log('Token exists but no cached permissions, reloading...')
          loadUserPermissions()
        }
      }
    }, 2000) // Check every 2 seconds
    
    return () => {
      window.removeEventListener('storage', handleStorageChange)
      window.removeEventListener('auth-state-changed', handleAuthChange)
      clearInterval(checkInterval)
    }
  }, [loadUserPermissions])
  
  // Load LLM configuration from localStorage on app initialization
  React.useEffect(() => {
    const loadSavedConfiguration = () => {
      try {
        const savedConfig = localStorage.getItem('llm-config')
        if (savedConfig) {
          const config = JSON.parse(savedConfig)
          
          // Restore supervisor LLM configuration
          if (config.supervisorLLM) {
            dispatch({ type: ActionTypes.SET_SUPERVISOR_LLM, payload: config.supervisorLLM })
          }
          
          // Restore per-provider model preferences
          if (config.models) {
            dispatch({ type: ActionTypes.SET_AGENT_MODELS, payload: config.models })
          }

          // Restore enabled agents configuration
          if (config.enabledAgents) {
            dispatch({ type: ActionTypes.SET_ENABLED_AGENTS, payload: config.enabledAgents })
          }
          
          // Restore summarization config (default used when nothing saved)
          if (config.summaryConfig) {
            dispatch({ type: ActionTypes.SET_SUMMARY_CONFIG, payload: config.summaryConfig })
          }
          
          // Restore auto mode configuration
          if (config.autoModeEnabled !== undefined) {
            dispatch({ type: ActionTypes.SET_AUTO_MODE_ENABLED, payload: config.autoModeEnabled })
          }
          
          if (config.autoModeRules) {
            dispatch({ type: ActionTypes.SET_AUTO_MODE_RULES, payload: config.autoModeRules })
          }

          if (config.allowUserModelSelection !== undefined) {
            dispatch({ type: ActionTypes.SET_ALLOW_USER_MODEL_SELECTION, payload: config.allowUserModelSelection })
          }
          
          console.log('LLM configuration loaded from localStorage:', {
            supervisorLLM: config.supervisorLLM,
            agentModels: config.models,
            enabledAgents: config.enabledAgents,
            summaryConfig: config.summaryConfig,
            autoModeEnabled: config.autoModeEnabled,
            autoModeRules: config.autoModeRules,
            timestamp: config.timestamp
          })
        }
        
        // ALWAYS ensure contextEnabled is true (override any saved state)
        // This ensures memory context is always used for queries
        const contextEnabled = localStorage.getItem('context-enabled')
        if (contextEnabled === 'false') {
          // If it was previously disabled, force it back to true
          localStorage.setItem('context-enabled', 'true')
          dispatch({ type: ActionTypes.SET_CONTEXT_ENABLED, payload: true })
          console.log('Context was disabled, forcing it back to TRUE')
        } else if (contextEnabled === null) {
          // First time, set it to true
          localStorage.setItem('context-enabled', 'true')
          console.log('Context enabled set to TRUE (default)')
        } else {
          // Already true, ensure state matches
          dispatch({ type: ActionTypes.SET_CONTEXT_ENABLED, payload: true })
          console.log('Context enabled confirmed as TRUE')
        }
      } catch (error) {
        console.error('Error loading LLM configuration from localStorage:', error)
      }
    }
    
    // Note: Chat sessions are now stored ONLY in the backend database
    // Each user can only access their own sessions via authenticated API calls
    // No localStorage is used for chat data to ensure security and privacy
    
    loadSavedConfiguration()
    // Chat sessions are loaded from backend API, not localStorage
  }, [])

  // Enforce dark theme globally on mount
  React.useEffect(() => {
    const enforceDarkMode = () => {
      if (state.theme !== 'dark') {
        dispatch({ type: ActionTypes.SET_THEME, payload: 'dark' })
      }
      document.documentElement.setAttribute('data-theme', 'dark')
      localStorage.setItem('app-theme', 'dark')
      console.log('Global Dark Mode Enforced')
    }

    enforceDarkMode()
  }, [state.theme])
  
  const value = {
    state,
    dispatch,
    actions: ActionTypes,
    showToast,
    setActiveWorkspace,
    loadUserPermissions // Expose function to reload permissions
  }
  
  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  )
}

// Custom hook
export function useAppContext() {
  const context = useContext(AppContext)
  if (!context) {
    throw new Error('useAppContext must be used within an AppProvider')
  }
  return context
}
